import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { startCase } from 'lodash';
import { MenuOutlined } from '@ant-design/icons';
import { Layout, Typography, Drawer, Space, Tag, theme, Grid, Button } from 'antd';
import { Loading } from '../../components';
import { useRightDrawer, drawerMap, MapRoutes, MapRoutesProps } from '../../router';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import './styles.css';

const { useToken } = theme;

type HeaderProps = {
  title: string | JSX.Element;
  buttons?: TitleButtonType[];
  btnProps: TitleButtonProps;
  id?: string;
  showTitle?: boolean;
};

function Header({ title, buttons, btnProps, id, showTitle = true }: HeaderProps) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  return (
    <>
      <div className="header-left">
        {showTitle ? (
          <Typography.Title className="title" level={3}>
            {title}
          </Typography.Title>
        ) : null}
        {id ? <Tag color="purple">{id}</Tag> : null}
      </div>

      <Space className="title-btns" size={isMobile ? 'small' : 'middle'}>
        {buttons?.map((ButtonComponent, index) => (
          <ButtonComponent key={`${index}`} {...btnProps} />
          // NOTE: Empty string to keep title-btns always in DOM (for Portals)
        )) ?? ''}
      </Space>
    </>
  );
}

function Main(prop: { routes: RouteItem[] }) {
  const useDrawerData: UseDrawerReturnType = useRightDrawer();
  const { token } = useToken();
  const { routes } = prop;
  const screens = Grid.useBreakpoint();
  const isMobile = useMemo(() => !screens.lg, [screens.lg]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  const getRouteComponent = useCallback<MapRoutesProps['render']>(
    (props) => {
      const { route, routeProps } = props;
      const { component: Component } = route;
      return (
        <>
          <Layout.Header className="title-bar" style={{ backgroundColor: token.colorBgContainer }}>
            <Header
              title={route.name}
              buttons={route.buttons}
              btnProps={{ ...routeProps, ...useDrawerData }}
              showTitle={route.showTitle}
            />
          </Layout.Header>
          <Layout.Content>
            <Component route={route} {...routeProps} {...useDrawerData} />
          </Layout.Content>
        </>
      );
    },
    [token.colorBgContainer, useDrawerData],
  );

  return (
    <>
      <Layout>
        {!isMobile ? (
          <Layout.Sider theme="light" className="app-sider" width={256}>
            <Sidebar routes={routes} drawerData={useDrawerData} />
          </Layout.Sider>
        ) : null}
        <Layout className="main">
          <Layout.Header className="navbar">
            <Navbar
              mobileMenuTrigger={
                isMobile ? (
                  <Button
                    type="text"
                    icon={<MenuOutlined />}
                    aria-label="Open sidebar"
                    onClick={() => setMobileSidebarOpen(true)}
                    className="mobile-sidebar-trigger"
                  />
                ) : null
              }
            />
          </Layout.Header>
          <Suspense fallback={<Loading size="large" />}>
            <MapRoutes routes={routes} render={getRouteComponent} />
          </Suspense>
        </Layout>
      </Layout>
      <Drawer
        placement="left"
        open={isMobile && mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        closable={false}
        width={280}
        className="mobile-sidebar-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <Sidebar
          routes={routes}
          drawerData={useDrawerData}
          onNavigate={() => setMobileSidebarOpen(false)}
        />
      </Drawer>
      {useDrawerData.drawers.map((drawer, index) => {
        const drawerChild = drawerMap[drawer.type];
        const { component: DrawerChildComponent } = drawerChild;
        const title =
          drawerChild.title || `${drawer.id ? 'Edit' : 'Add'} ${startCase(drawer.type)}`;

        return (
          <Drawer
            key={`${drawer.type}_${drawer.id}_${index}`}
            title={
              <Header
                title={title}
                buttons={drawerChild.buttons}
                btnProps={useDrawerData}
                id={drawerChild.title ? undefined : drawer.id}
              />
            }
            maskClosable={false}
            closable={false}
            push={false}
            open={drawer.visible}
            width={1000 - 24 * index}
          >
            {drawer.visible ? (
              <Suspense fallback={<Loading size="large" />}>
                <DrawerChildComponent id={drawer.id} {...useDrawerData} initialData={drawer.data} />
              </Suspense>
            ) : null}
          </Drawer>
        );
      })}
    </>
  );
}

export default Main;
